import { Theme } from 'styles/theme';
import * as Yup from 'yup';

export const ALLOWED_SYMBOLS_ERROR = 'allowed_symbols';
export const DIACRITIC_LETTERS =
  'àâæçéèêëîïôœùûüÿáéíóúüñáâãàçéêíóôõúüàèéìíîòóùäöüßéèëïöéèëäöüæøååäöåæøáéíóúýþæöáíóúýæøäöąćęłńóśźżáčďéěíňóřšťúůýžáäčďéíľĺňóôŕšťúýžáéíóöőúüűčšžčćđšžāčēģīķļņšūžąčęėįšųūžäöüõăâîșțçğıöşüÀÂÆÇÉÈÊËÎÏÔŒÙÛÜŸÁÉÍÓÚÜÑÁÂÃÀÇÉÊÍÓÔÕÚÜÀÈÉÌÍÎÒÓÙÄÖÜÉÈËÏÖÉÈËÄÖÜÆØÅÅÄÖÅÆØÁÉÍÓÚÝÞÆÖÁÍÓÚÝÆØÄÖĄĆĘŁŃÓŚŹŻÁČĎÉĚÍŇÓŘŠŤÚŮÝŽÁÄČĎÉÍĽĹŇÓÔŔŠŤÚÝŽÁÉÍÓÖŐÚÜŰČŠŽČĆĐŠŽĀČĒĢĪĶĻŅŠŪŽĄČĘĖĮŠŲŪŽÄÖÜÕĂÂÎȘȚÇĞIÖŞÜ';
export const ALLOWED_SPECIAL_CHARACTERS = `.,:;!+\\-_—/\\\\|()<>„“"´\`'’#*&©®™@№%`;

export const allowedGeneralCharsRegex = new RegExp(`^[A-Za-z0-9${DIACRITIC_LETTERS}${ALLOWED_SPECIAL_CHARACTERS}\\s]*$`);
export const allowedWebsiteCharsRegex = new RegExp(`^[A-Za-z0-9${DIACRITIC_LETTERS}${ALLOWED_SPECIAL_CHARACTERS}\\s?=]*$`);
export const allowedHostnameCharsRegex = new RegExp(`^[A-Za-z0-9${DIACRITIC_LETTERS}._-]+$`);
export const allowedSymbolsLink = 'https://www.environdec.com/resources/allowed-characters';

const ensureProtocol = (s: string) => {
  const v = s.trim();
  if (/^(https?|ftp):\/\//i.test(v)) return v;
  return `https://${v}`;
};

const extractRawHost = (input: string) => {
  const s = input.trim();

  const noProto = s.replace(/^(https?|ftp):\/\//i, '');
  const untilSlash = noProto.split(/[/?#]/)[0];

  const afterAt = untilSlash.includes('@') ? untilSlash.split('@').pop()! : untilSlash;
  return afterAt.split(':')[0].trim(); // remove :port
};

export const websiteSmartValidation = (t: any) =>
  (Yup.string() as Yup.StringSchema<string | null | undefined>)
    // A) allowed characters for the FULL input
    .test('website-allowed-chars', ALLOWED_SYMBOLS_ERROR, function (value) {
      if (value == null || value.trim() === '') return true;
      return allowedWebsiteCharsRegex.test(value.trim());
    })
    // B) structure + hostname rules
    .test('website-url-shape', t('messages.invalidUrl'), function (value) {
      const { createError, path } = this;
      if (value == null || value.trim() === '') return true;

      const input = value.trim();
      const urlLike = ensureProtocol(input);

      let url: URL;
      try {
        url = new URL(urlLike);
      } catch {
        return createError({ path, message: t('messages.invalidUrl') });
      }

      if (!['http:', 'https:', 'ftp:'].includes(url.protocol)) {
        return createError({ path, message: t('messages.invalidUrl') });
      }

      const rawHost = extractRawHost(input);

      // forbid empty host and bad dot patterns
      if (!rawHost) return createError({ path, message: t('messages.invalidUrl') });
      if (rawHost.startsWith('.') || rawHost.endsWith('.')) return createError({ path, message: t('messages.invalidUrl') });
      if (rawHost.includes('..')) return createError({ path, message: t('messages.invalidUrl') });

      const labels = rawHost.split('.');
      if (labels.length < 2) return createError({ path, message: t('messages.invalidUrl') });
      if (labels.some((x) => x.length === 0)) return createError({ path, message: t('messages.invalidUrl') });

      // forbid labels starting/ending with '-' (e.g. "-abc.com" or "abc-.com")
      if (labels.some((l) => l.startsWith('-') || l.endsWith('-'))) {
        return createError({ path, message: t('messages.invalidUrl') });
      }

      // hostname allowed chars (diacritics + dot + dash etc.)
      if (!allowedHostnameCharsRegex.test(rawHost)) {
        return createError({ path, message: ALLOWED_SYMBOLS_ERROR });
      }

      return true;
    });

export const allowedEmailCharsRegex = new RegExp(`^[A-Za-z0-9${DIACRITIC_LETTERS}${ALLOWED_SPECIAL_CHARACTERS}\\s]*$`);

export const emailSmartValidation = (t: any) =>
  (Yup.string() as Yup.StringSchema<string | null | undefined>)
    // A) allowed characters
    .test('email-allowed-chars', ALLOWED_SYMBOLS_ERROR, function (value) {
      if (value == null || value.trim() === '') return true;
      return allowedEmailCharsRegex.test(value.trim());
    })
    // B) shape (standard email)
    .test('email-shape', t('messages.invalidEmail'), function (value) {
      const { createError, path } = this;
      if (value == null || value.trim() === '') return true;

      const v = value.trim();
      const ok = Yup.string().email().isValidSync(v);

      return ok ? true : createError({ path, message: t('messages.invalidEmail') });
    });

export const htmlToPlainText = (html: string) => {
  if (!html) return '';

  const div = document.createElement('div');
  div.innerHTML = html;

  const text = div.textContent ?? '';

  return text.replace(/\u00A0/g, ' ').trim();
};

export const richTextSmartValidation = (t: any) =>
  Yup.string().test('richtext-smart-validation', t('messages.required'), function (value) {
    const { createError, path } = this;

    if (value == null || value === '') return true;

    const plain = htmlToPlainText(value);

    if (!plain) return true;

    if (!allowedGeneralCharsRegex.test(plain)) {
      return createError({ path, message: ALLOWED_SYMBOLS_ERROR });
    }

    return true;
  });

export const renderAllowedSymbolsError = (args: {
  theme: Theme;
  allowedSymbolsLink: string;
  style?: React.CSSProperties;
}) => {
  const { theme, allowedSymbolsLink, style } = args;

  return (
    <span style={{ color: theme.colors.systemStatusError, fontFamily: 'Inter', fontSize: '12px', ...style }}>
      Please use only permitted symbols. View the full list{' '}
      <a
        href={allowedSymbolsLink}
        target="_blank"
        rel="noreferrer"
        style={{ textDecoration: 'underline', color: 'inherit' }}
      >
        here
      </a>
      .
    </span>
  );
};

export const createAllowedSymbolsTextInputProps = (args: {
  theme: Theme;
  requiredMsg: string;
  allowedSymbolsLink: string;
}) => {
  const { theme, requiredMsg, allowedSymbolsLink } = args;

  return {
    shouldShowTooltipError: (err: string) => err === requiredMsg,
    shouldShowBottomError: (err: string) => err === ALLOWED_SYMBOLS_ERROR,
    shouldHighlightBorder: (err: string) => err === ALLOWED_SYMBOLS_ERROR,
    renderErrorMessage: (err: string) => {
      if (err !== ALLOWED_SYMBOLS_ERROR)
        return <span style={{ color: theme.colors.systemStatusError, fontFamily: 'Inter', fontSize: '12px' }}>{err}</span>;

      return renderAllowedSymbolsError({
        theme,
        allowedSymbolsLink,
      });
    },
  };
};

export const createAllowedSymbolsBottomErrorsTextInputProps = (args: {
  theme: Theme;
  requiredMsg: string;
  allowedSymbolsLink: string;
}) => {
  const base = createAllowedSymbolsTextInputProps(args);

  return {
    ...base,

    shouldShowTooltipError: () => false,
    shouldShowBottomError: () => true,
    shouldHighlightBorder: (err: string) => err === ALLOWED_SYMBOLS_ERROR,
  };
};
